import { StyleSheet, TextStyle } from 'react-native'

import { CSSObject } from 'styled-components'
import styled from 'styled-components/native'

import {
  FFM_COMPONENT_KEY,
  FFM_COMPONENT_PROP_KEY,
  FONT_STYLISTIC_SET,
  FontFamilyMetadata,
  getFontFamilyFromMetadata,
  SUPPORTED_FONT_FAMILY,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/fontFamilyMetadata'
import { ThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'

import { IS_LIBRARY, IS_WEB } from '../constants'
import { FONT_FAMILY } from './fontFamilies'

export enum TextType {
  DISPLAY = 'display',
  TITLE = 'title',
  BODY = 'body',
  CTA = 'cta',
}
export enum DisplaySize {
  LARGE = 'large',
  MEDIUM = 'medium',
  SMALL = 'small',
  XSMALL = 'xsmall',
  EYEBROW = 'eyebrow',
}
export enum TitleSize {
  XLARGE = 'xlarge',
  LARGE = 'large',
  MEDIUM = 'medium',
  SMALL = 'small',
  XSMALL = 'xsmall',
}
export enum BodyTextSize {
  XLARGE = 'xlarge',
  MEDLARGE = 'medlarge', // Pending design approval
  LARGE = 'large',
  DEFAULT = 'default',
  SMALL = 'small',
  CAPTION = 'caption',
  BADGE = 'badge',
}
enum CTASize {
  BUTTON = 'button',
}

/**
 * Card title line-height: title-xsmall (16px) at the Figma 1.2 leading. Tighter than the default
 * XSMALL title token so a 2-line title fits a fixed-height card.
 */
export const CARD_TITLE_LINE_HEIGHT = 19.2

export type TextSize = DisplaySize | TitleSize | BodyTextSize | CTASize

export type TextAlign = 'left' | 'right' | 'center'

export type FontFamilyStyling = {
  fontFamily: string
  fontWeight?: TextStyle['fontWeight']
}

// React Native accepts a space-separated string at runtime, while its public type expects an array.
// styled-components/native requires that string so it can parse the declaration into the native array.
type CSSAndNativeFontVariant = NonNullable<TextStyle['fontVariant']> & NonNullable<CSSObject['fontVariant']>
type NativeFontVariant = NonNullable<TextStyle['fontVariant']>[number]

type FontFamilyMetadataStyling = FontFamilyStyling & {
  fontFeatureSettings?: string
  fontVariant?: CSSAndNativeFontVariant
}

const WEB_FONT_FEATURE_BY_STYLISTIC_SET: Record<FONT_STYLISTIC_SET, string> = {
  [FONT_STYLISTIC_SET.SS01]: '"ss01" 1',
  [FONT_STYLISTIC_SET.SS02]: '"ss02" 1',
}

const NATIVE_FONT_VARIANT_BY_STYLISTIC_SET: Record<FONT_STYLISTIC_SET, NativeFontVariant> = {
  [FONT_STYLISTIC_SET.SS01]: 'stylistic-one',
  [FONT_STYLISTIC_SET.SS02]: 'stylistic-two',
}

// Font weights
const LIGHT = '300' as const
const REGULAR = '400' as const
const MEDIUM = '500' as const
const BOLD = '700' as const
const HEAVY = '900' as const

// When exporting Ui-Core-Mobile components for the web we need to map the fonts to the declarations made in Ui-Core
export const moderatBold: FontFamilyStyling = IS_LIBRARY
  ? { fontFamily: 'moderat', fontWeight: BOLD }
  : { fontFamily: FONT_FAMILY.MODERAT_BOLD }
export const moderatMedium: FontFamilyStyling = IS_LIBRARY
  ? { fontFamily: 'moderat', fontWeight: MEDIUM }
  : { fontFamily: FONT_FAMILY.MODERAT_MEDIUM }
export const moderatRegular: FontFamilyStyling = IS_LIBRARY
  ? { fontFamily: 'moderat', fontWeight: REGULAR }
  : { fontFamily: FONT_FAMILY.MODERAT_REGULAR }
export const moderatLight: FontFamilyStyling = IS_LIBRARY
  ? { fontFamily: 'moderat', fontWeight: LIGHT }
  : { fontFamily: FONT_FAMILY.MODERAT_LIGHT }
export const moderatPlain: FontFamilyStyling = IS_LIBRARY
  ? { fontFamily: 'moderat' }
  : { fontFamily: FONT_FAMILY.MODERAT_REGULAR }

const nunitoBold: FontFamilyStyling = {
  fontFamily: FONT_FAMILY.NUNITO,
  fontWeight: BOLD,
}
const nunitoMedium: FontFamilyStyling = {
  fontFamily: FONT_FAMILY.NUNITO,
  fontWeight: MEDIUM,
}
const nunitoRegular: FontFamilyStyling = {
  fontFamily: FONT_FAMILY.NUNITO,
  fontWeight: REGULAR,
}
const nunitoPlain: FontFamilyStyling = {
  fontFamily: FONT_FAMILY.NUNITO,
  fontWeight: REGULAR,
}
const nunitoLight: FontFamilyStyling = {
  fontFamily: FONT_FAMILY.NUNITO,
  fontWeight: LIGHT,
}

export const gazpachoHeavy: FontFamilyStyling = IS_LIBRARY
  ? { fontFamily: 'Gazpacho', fontWeight: HEAVY }
  : { fontFamily: FONT_FAMILY.GAZPACHO_HEAVY }

export const ppFragmentBold: FontFamilyStyling = IS_LIBRARY
  ? { fontFamily: 'PP Fragment', fontWeight: BOLD }
  : { fontFamily: FONT_FAMILY.PP_FRAGMENT_BOLD }
const ppFragmentMedium: FontFamilyStyling = IS_LIBRARY
  ? { fontFamily: 'PP Fragment', fontWeight: MEDIUM }
  : { fontFamily: FONT_FAMILY.PP_FRAGMENT_MEDIUM }
const ppFragmentRegular: FontFamilyStyling = IS_LIBRARY
  ? { fontFamily: 'PP Fragment', fontWeight: REGULAR }
  : { fontFamily: FONT_FAMILY.PP_FRAGMENT_REGULAR }
const ppFragmentPlain: FontFamilyStyling = IS_LIBRARY
  ? { fontFamily: 'PP Fragment' }
  : { fontFamily: FONT_FAMILY.PP_FRAGMENT_REGULAR }
export const ppFragmentLight: FontFamilyStyling = IS_LIBRARY
  ? { fontFamily: 'PP Fragment', fontWeight: LIGHT }
  : { fontFamily: FONT_FAMILY.PP_FRAGMENT_LIGHT }

export enum FONT_FAMILY_AND_WEIGHT {
  BOLD,
  MEDIUM,
  REGULAR,
  PLAIN,
  LIGHT,
}

export const getSupportedFontStyling = (
  fontFamily: SUPPORTED_FONT_FAMILY,
  fontFamilyAndWeightType: FONT_FAMILY_AND_WEIGHT,
): FontFamilyStyling => {
  if (fontFamily === SUPPORTED_FONT_FAMILY.GAZPACHO) {
    return gazpachoHeavy
  } else if (fontFamily === SUPPORTED_FONT_FAMILY.NUNITO) {
    switch (fontFamilyAndWeightType) {
      case FONT_FAMILY_AND_WEIGHT.BOLD:
        return nunitoBold
      case FONT_FAMILY_AND_WEIGHT.MEDIUM:
        return nunitoMedium
      case FONT_FAMILY_AND_WEIGHT.LIGHT:
        return nunitoLight
      case FONT_FAMILY_AND_WEIGHT.REGULAR:
        return nunitoRegular
      case FONT_FAMILY_AND_WEIGHT.PLAIN:
        return nunitoPlain
      default:
        return nunitoPlain
    }
  } else if (fontFamily === SUPPORTED_FONT_FAMILY.PP_FRAGMENT) {
    switch (fontFamilyAndWeightType) {
      case FONT_FAMILY_AND_WEIGHT.BOLD:
        return ppFragmentBold
      case FONT_FAMILY_AND_WEIGHT.MEDIUM:
        return ppFragmentMedium
      case FONT_FAMILY_AND_WEIGHT.LIGHT:
        return ppFragmentLight
      case FONT_FAMILY_AND_WEIGHT.REGULAR:
        return ppFragmentRegular
      case FONT_FAMILY_AND_WEIGHT.PLAIN:
        return ppFragmentPlain
      default:
        return ppFragmentPlain
    }
  } else {
    switch (fontFamilyAndWeightType) {
      case FONT_FAMILY_AND_WEIGHT.BOLD:
        return moderatBold
      case FONT_FAMILY_AND_WEIGHT.MEDIUM:
        return moderatMedium
      case FONT_FAMILY_AND_WEIGHT.REGULAR:
        return moderatRegular
      case FONT_FAMILY_AND_WEIGHT.PLAIN:
        return moderatPlain
      case FONT_FAMILY_AND_WEIGHT.LIGHT:
        return moderatLight
      default:
        return moderatPlain
    }
  }
}

// Helper to determine body text weight - respects useRegularBodyWeight override for Aurora themes
export const getBodyTextWeight = (fontFamilyMetadata: FontFamilyMetadata): FONT_FAMILY_AND_WEIGHT => {
  if (fontFamilyMetadata.isAuroraMetadata && fontFamilyMetadata.useRegularBodyWeight) {
    return FONT_FAMILY_AND_WEIGHT.REGULAR
  }
  return fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.REGULAR
}

export const getFontStylingFromMetadata = (
  fontFamilyMetadata: FontFamilyMetadata,
  fontFamilyAndWeightType: FONT_FAMILY_AND_WEIGHT,
  componentKey?: FFM_COMPONENT_KEY,
  componentPropKey?: FFM_COMPONENT_PROP_KEY,
): FontFamilyMetadataStyling => {
  const fontFamily = getFontFamilyFromMetadata(fontFamilyMetadata, componentKey, componentPropKey)
  const stylisticSets = fontFamilyMetadata.fontFamilyOptions?.[fontFamily]?.stylisticSets

  return {
    ...getSupportedFontStyling(fontFamily, fontFamilyAndWeightType),
    ...(IS_WEB && stylisticSets
      ? {
          fontFeatureSettings: stylisticSets
            .map((stylisticSet) => WEB_FONT_FEATURE_BY_STYLISTIC_SET[stylisticSet])
            .join(', '),
        }
      : {}),
    ...(!IS_WEB && stylisticSets
      ? {
          // styled-components/native parses font variants from a space-separated declaration into a React Native array.
          fontVariant: stylisticSets
            .map((stylisticSet) => NATIVE_FONT_VARIANT_BY_STYLISTIC_SET[stylisticSet])
            .join(' ') as CSSAndNativeFontVariant,
        }
      : {}),
  }
}

export const getFontStyles = (
  colors: ThemeColors,
  fontFamilyMetadata: FontFamilyMetadata,
  uppercaseEyebrows = false,
) => ({
  [TextType.DISPLAY]: {
    [DisplaySize.LARGE]: {
      ...getFontStylingFromMetadata(
        fontFamilyMetadata,
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.BOLD,
        FFM_COMPONENT_KEY.BASE_TEXT,
        FFM_COMPONENT_PROP_KEY.DISPLAY,
      ),
      fontSize: 56,
      lineHeight: 70,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [DisplaySize.MEDIUM]: {
      ...getFontStylingFromMetadata(
        fontFamilyMetadata,
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.BOLD,
        FFM_COMPONENT_KEY.BASE_TEXT,
        FFM_COMPONENT_PROP_KEY.DISPLAY,
      ),
      fontSize: 48,
      lineHeight: 62,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [DisplaySize.SMALL]: {
      ...getFontStylingFromMetadata(
        fontFamilyMetadata,
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.BOLD,
        FFM_COMPONENT_KEY.BASE_TEXT,
        FFM_COMPONENT_PROP_KEY.DISPLAY,
      ),
      fontSize: 34,
      lineHeight: 44,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [DisplaySize.XSMALL]: {
      ...getFontStylingFromMetadata(
        fontFamilyMetadata,
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.BOLD,
        FFM_COMPONENT_KEY.BASE_TEXT,
        FFM_COMPONENT_PROP_KEY.DISPLAY,
      ),
      fontSize: 28,
      lineHeight: 36,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [DisplaySize.EYEBROW]: {
      ...getFontStylingFromMetadata(
        fontFamilyMetadata,
        uppercaseEyebrows && fontFamilyMetadata.isAuroraMetadata
          ? FONT_FAMILY_AND_WEIGHT.MEDIUM
          : FONT_FAMILY_AND_WEIGHT.BOLD,
      ),
      fontSize: 12,
      lineHeight: 18,
      textTransform: uppercaseEyebrows ? 'uppercase' : 'none',
      letterSpacing: uppercaseEyebrows ? 0.5 : 0,
      color: colors.textPrimary,
    } as CSSObject | TextStyle,
  },
  [TextType.TITLE]: {
    [TitleSize.XLARGE]: {
      ...getFontStylingFromMetadata(
        fontFamilyMetadata,
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.MEDIUM,
        FFM_COMPONENT_KEY.BASE_TEXT,
        FFM_COMPONENT_PROP_KEY.TITLE,
      ),
      fontSize: 34,
      lineHeight: 44,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [TitleSize.LARGE]: {
      ...getFontStylingFromMetadata(
        fontFamilyMetadata,
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.MEDIUM,
        FFM_COMPONENT_KEY.BASE_TEXT,
        FFM_COMPONENT_PROP_KEY.TITLE,
      ),
      fontSize: 28,
      lineHeight: 36,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [TitleSize.MEDIUM]: {
      ...getFontStylingFromMetadata(
        fontFamilyMetadata,
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.MEDIUM,
        FFM_COMPONENT_KEY.BASE_TEXT,
        FFM_COMPONENT_PROP_KEY.TITLE,
      ),
      fontSize: 22,
      lineHeight: 28,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [TitleSize.SMALL]: {
      ...getFontStylingFromMetadata(
        fontFamilyMetadata,
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.MEDIUM,
        FFM_COMPONENT_KEY.BASE_TEXT,
        FFM_COMPONENT_PROP_KEY.TITLE,
      ),
      fontSize: 18,
      lineHeight: 24,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [TitleSize.XSMALL]: {
      ...getFontStylingFromMetadata(
        fontFamilyMetadata,
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.MEDIUM,
        FFM_COMPONENT_KEY.BASE_TEXT,
        FFM_COMPONENT_PROP_KEY.TITLE,
      ),
      fontSize: 16,
      lineHeight: 22,
      textTransform: 'none',
      color: colors.textPrimary,
    },
  },
  [TextType.BODY]: {
    [BodyTextSize.XLARGE]: {
      ...getFontStylingFromMetadata(fontFamilyMetadata, getBodyTextWeight(fontFamilyMetadata)),
      fontSize: 34,
      lineHeight: 44,
      color: colors.textPrimary,
    },
    [BodyTextSize.MEDLARGE]: {
      ...getFontStylingFromMetadata(fontFamilyMetadata, getBodyTextWeight(fontFamilyMetadata)),
      fontSize: 28,
      lineHeight: 39,
      color: colors.textPrimary,
    },
    [BodyTextSize.LARGE]: {
      ...getFontStylingFromMetadata(fontFamilyMetadata, getBodyTextWeight(fontFamilyMetadata)),
      fontSize: 18,
      lineHeight: 28,
      color: colors.textPrimary,
    },
    [BodyTextSize.DEFAULT]: {
      ...getFontStylingFromMetadata(fontFamilyMetadata, getBodyTextWeight(fontFamilyMetadata)),
      fontSize: 16,
      lineHeight: 24,
      color: colors.textPrimary,
    },
    [BodyTextSize.SMALL]: {
      ...getFontStylingFromMetadata(fontFamilyMetadata, getBodyTextWeight(fontFamilyMetadata)),
      fontSize: 14,
      lineHeight: 20,
      color: colors.textPrimary,
    },
    [BodyTextSize.CAPTION]: {
      ...getFontStylingFromMetadata(fontFamilyMetadata, getBodyTextWeight(fontFamilyMetadata)),
      fontSize: 12,
      lineHeight: 18,
      color: colors.textPrimary,
    },
    [BodyTextSize.BADGE]: {
      ...getFontStylingFromMetadata(
        fontFamilyMetadata,
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.REGULAR : FONT_FAMILY_AND_WEIGHT.BOLD,
      ),
      fontSize: 12,
      lineHeight: 18,
      color: colors.textPrimary,
    },
  },
  [TextType.CTA]: {
    [CTASize.BUTTON]: {
      ...getFontStylingFromMetadata(fontFamilyMetadata, FONT_FAMILY_AND_WEIGHT.MEDIUM),
      fontSize: 16,
      lineHeight: 24,
    },
  },
})

export const getTypeStyles = (colors: ThemeColors, fontFamilyMetadata: FontFamilyMetadata) => {
  const fontStyles = getFontStyles(colors, fontFamilyMetadata)
  return StyleSheet.create({
    displayLarge: fontStyles.display.large,
    displayMedium: fontStyles.display.medium,
    displaySmall: fontStyles.display.small,
    titleLarge: fontStyles.title.large,
    titleMedium: fontStyles.title.medium,
    titleSmall: fontStyles.title.small,
    titleXsmall: fontStyles.title.xsmall,
    displayEyebrow: fontStyles.display.eyebrow,
    bodyLarge: fontStyles.body.large,
    bodyDefault: fontStyles.body.default,
    bodySmall: fontStyles.body.small,
    bodyCaption: fontStyles.body.caption,
    ctaButton: fontStyles.cta.button,
    em: { fontStyle: 'italic' },
  } as { [key: string]: TextStyle })
}

// This component is allowed to use styled.Text since it is the root component for all Text in the mobile app
// eslint-disable-next-line lyrahealth/no-react-native-text
export const StyledText = styled.Text<{ type: TextType; size: TextSize }>(({ theme, type, size }) => {
  const style = getFontStyles(theme.colors, theme.fontFamilyMetadata, theme.uppercaseEyebrows)?.[type]?.[size]
  if (style) {
    const styleWithUnits = { ...style, lineHeight: `${style.lineHeight}px` }
    return styleWithUnits as CSSObject
  } else {
    console.warn(`StyledText: The type style ${type} ${size} does not exist`)
    return {} as CSSObject
  }
})

export const getHighlightedText = (colors: ThemeColors) => ({ color: colors.textActive, fontStyle: 'normal' })
